import _ from "underscore";

export function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export const mapKeys = <
  T extends Record<K, string | number | symbol>,
  K extends keyof T,
>(
  objArr: T[],
  key: K,
): Record<string | number | symbol, T> => {
  const objArrClone = deepClone(objArr);
  return objArrClone.reduce(
    (obj, item) => {
      obj[item[key]] = item;
      return obj;
    },
    {} as Record<string | number | symbol, T>,
  );
};

export const isFile = (obj: unknown) => obj instanceof File;

export const deduplicateArrayOfObjects = <T extends Record<string, unknown>>(
  array: T[],
  uniqKey: string | ((a: T) => string | number) = "id",
): T[] => {
  const deduped: T[] = [];
  const existingKeys: string[] = [];

  const getKey = (item: T) =>
    _.isFunction(uniqKey) ? uniqKey(item) : item[uniqKey];

  for (const item of array) {
    const key = getKey(item);

    if (!_.contains(existingKeys, key)) {
      existingKeys.push(key as string);
      deduped.push(item);
    }
  }

  return deduped;
};

// https://stackoverflow.com/a/49524571
// We want to treat first argument as the more important one/most recent when comparing
export const getObjectsDifferences = <
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
>(
  firstObject: T,
  objectToCompare?: U,
): Partial<T> & Partial<U> => {
  const changes = (
    object: Record<string, unknown>,
    secondObject?: Record<string, unknown>,
  ): Partial<T> & Partial<U> => {
    if (!secondObject) {
      return object as Partial<T> & Partial<U>;
    }

    const change = _.pick(
      {
        ..._.mapObject(secondObject, (value, key) => {
          const areObjectsEqual = _.isEqual(value, object[key]);

          if (areObjectsEqual) {
            return undefined;
          }

          if (_.isObject(value) && _.isObject(object[key])) {
            return changes(value, object);
          }

          return value;
        }),
        // required to check keys that secondObject has, but first one doesn't
        ..._.mapObject(object, (value, key) => {
          const areObjectsEqual = _.isEqual(value, secondObject[key]);

          if (areObjectsEqual) {
            return undefined;
          }

          if (_.isObject(value) && _.isObject(secondObject[key])) {
            return changes(value, secondObject);
          }

          return value;
        }),
      },
      (value) => {
        return value !== undefined;
      },
    );

    return change as Partial<T> & Partial<U>;
  };

  return changes(firstObject, objectToCompare);
};

export const parsePrimitive = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return value.toString();
  }
};

const isDirtyPrimitiveValue = (value: unknown) => {
  return typeof value === "undefined" || value === null || value === "";
};
export const clearEmpties = <T extends Record<string | number, unknown>>(
  obj: T,
): DeepPartial<T> => {
  // Recursive function
  Object.keys(obj).forEach((key: string) => {
    const val = obj[key];
    if (isDirtyPrimitiveValue(val)) {
      delete obj[key];

      return;
    }

    if (!val || typeof val !== "object") {
      return;
    }

    clearEmpties(val as Record<string, unknown>);
    if (!Object.keys(val).length) {
      delete obj[key];
    } else if (Array.isArray(val)) {
      (obj[key] as unknown[]) = val.filter(
        (arrayValue) => !isDirtyPrimitiveValue(arrayValue),
      );
    }
  });

  return obj;
};

type RecordWithoutNulls<T extends Record<string | number, unknown>> = {
  [K in keyof T]: Exclude<T[K], null>;
};
export const removeNullValues = <T extends Record<string | number, unknown>>(
  object: T,
): RecordWithoutNulls<T> => {
  const objectCopy: Partial<T> = { ...object };
  for (const key in objectCopy) {
    if (objectCopy[key] === null) {
      objectCopy[key] = undefined;
    }
  }

  return objectCopy as RecordWithoutNulls<T>;
};

// example [1,2,3,4,5] by 2 => [[1,2],[3,4],[5]]
export const paginateArrayBy = (items: unknown[], by: number) => {
  return items.reduce<Array<Array<unknown>>>((prev, current, index) => {
    if (index % by === 0) {
      prev.push([]);
    }

    prev[prev.length - 1].push(current);

    return prev;
  }, []);
};

export const combineResults = <
  T extends { id: string | number } = { id: string | number },
>(
  cachedResults: T[],
  newResults: T[],
): T[] => {
  const combinedResults: T[] = [];
  const newResultsCopy = [...newResults];

  // keep the right order of results by pushing cached first, not included in new results
  for (const cachedResult of cachedResults) {
    const newResultIndex = newResultsCopy.findIndex(
      ({ id }) => id === cachedResult.id,
    );
    if (newResultIndex !== -1) {
      const [newResult] = newResultsCopy.splice(newResultIndex, 1);
      combinedResults.push(newResult);
    } else {
      combinedResults.push(cachedResult);
    }
  }

  combinedResults.push(...newResultsCopy);

  return combinedResults;
};

export const convertValuesToString = (
  obj: Record<string, unknown>,
): Record<string, string> => {
  return _.mapObject(obj, (value) => String(value));
};

export const removeIds = <T extends { id?: number }>(
  entries?: T[],
): Omit<T, "id">[] => {
  if (!entries) {
    return [];
  }

  return entries.map((entry) => {
    const entryCopy = { ...entry };
    delete entryCopy.id;

    return entryCopy;
  });
};

export const extractExtensionFromFilename = (
  filename: string,
  dotPrefix = true,
) => {
  const dotIndex = filename.lastIndexOf(".");

  return filename.substring(dotPrefix ? dotIndex : dotIndex + 1);
};
